import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { getToken, onMessage } from "firebase/messaging";
import soundManager from "../services/SoundManager";
import {
  getFirebaseMessaging,
  getMissingFirebaseConfigKeys,
  hasFirebaseConfig,
} from "../services/firebase";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
const FIREBASE_ENV_HINT =
  "Add the Firebase web app config to spike-frontend/.env.local and restart the frontend dev server.";
const SESSION_TOKEN_KEY = "spike_auth_token";
const FCM_TOKEN_KEY = "spike_fcm_token";
const PUSH_ONBOARDING_KEY = "spike_push_onboarding_pending";
const ZONE_SOUND_DELAY_SECONDS_DEFAULT = 10;
const ALERT_DEDUPLICATION_WINDOW_MS = 4000;
const ORIGINAL_TITLE =
  typeof document !== "undefined" ? document.title : "Spike";

function getAuthHeaders() {
  const token = window.localStorage.getItem(SESSION_TOKEN_KEY);

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  };
}

function buildNotificationState() {
  if (typeof Notification === "undefined") {
    return "unsupported";
  }

  return Notification.permission;
}

function normalizeAlert(payload, fallbackEvent) {
  const eventType = payload.eventType || payload.event || fallbackEvent;
  const zoneType = payload.zone?.type || payload.zoneType || "";

  return {
    ...payload,
    eventType,
    zone: payload.zone || {
      type: zoneType,
    },
    price: Number(payload.price || 0),
  };
}

function getAlertLabel(eventType) {
  if (eventType === "market_in_zone") {
    return "Zone Active";
  }

  if (eventType === "spike_detected") {
    return "Spike Confirmed";
  }

  if (eventType === "zone_failed") {
    return "Zone Failed";
  }

  return "Zone Nearby";
}

function buildAlertDeduplicationKey(alert) {
  return (
    alert.id ||
    [
      alert.eventType || "event",
      alert.symbol || "symbol",
      alert.zone?.id || alert.zoneId || alert.zone?.type || "zone",
      alert.createdAt || alert.time || alert.timestamp || "time",
      Number(alert.price || 0).toFixed(2),
    ].join(":")
  );
}

export default function useTradingAlerts({
  symbol,
  timeframe,
  user,
  currentPrice,
  analysis,
}) {
  const [alerts, setAlerts] = useState([]);
  const [permission, setPermission] = useState(buildNotificationState);
  const [pushEnabled, setPushEnabled] = useState(() =>
    Boolean(window.localStorage.getItem(FCM_TOKEN_KEY))
  );
  const [zoneSoundDelaySeconds, setZoneSoundDelaySeconds] = useState(
    ZONE_SOUND_DELAY_SECONDS_DEFAULT
  );
  const [showPushOnboarding, setShowPushOnboarding] = useState(() =>
    window.localStorage.getItem(PUSH_ONBOARDING_KEY) === "1" &&
    !window.localStorage.getItem(FCM_TOKEN_KEY)
  );
  const [pushDebugMessage, setPushDebugMessage] = useState("");
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketDebugMessage, setSocketDebugMessage] = useState("");
  const [watchSynced, setWatchSynced] = useState(false);
  const [lastAlert, setLastAlert] = useState(null);
  const titleIntervalRef = useRef(null);
  const swRegistrationRef = useRef(null);
  const testEventIndexRef = useRef(0);
  const zoneSoundIntervalRef = useRef(null);
  const zoneSoundLoopKeyRef = useRef("");
  const recentAlertKeysRef = useRef(new Map());
  const lastZoneOutcomeEventIdRef = useRef("");
  const socketRef = useRef(null);

  const stopTitleFlash = useCallback(() => {
    if (!titleIntervalRef.current) {
      return;
    }

    window.clearInterval(titleIntervalRef.current);
    titleIntervalRef.current = null;
  }, []);

  const startTitleFlash = useCallback(() => {
    if (titleIntervalRef.current) {
      return;
    }

    let showAlert = false;
    titleIntervalRef.current = window.setInterval(() => {
      showAlert = !showAlert;
      document.title = showAlert ? "SPIKE ALERT" : ORIGINAL_TITLE;
    }, 1000);
  }, []);

  const dismissDeliveredNotifications = useCallback(async () => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    try {
      const registration =
        swRegistrationRef.current || (await navigator.serviceWorker.ready);
      const notifications = await registration.getNotifications({
        includeTriggered: true,
      });

      notifications.forEach((notification) => notification.close());
    } catch (error) {}
  }, []);

  const showLocalHiddenNotification = useCallback(
    async (alert) => {
      if (permission !== "granted") {
        return;
      }

      try {
        const registration =
          swRegistrationRef.current || (await navigator.serviceWorker.ready);

        await registration.showNotification(getAlertLabel(alert.eventType), {
          body: `${alert.symbol} ${alert.zone?.type || ""} at ${Number(
            alert.price
          ).toFixed(2)}`,
          icon: "/icon.png",
          vibrate: [200, 100, 300],
          silent: false,
          sound: "default",
          requireInteraction: true,
        });
        console.debug("[alerts push] local notification displayed", {
          eventType: alert.eventType,
          symbol: alert.symbol,
          price: alert.price,
        });
      } catch (error) {}
    },
    [permission]
  );

  const queueBackgroundFallback = useCallback(
    (alert) => {
      soundManager.play(alert.eventType);

      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate([200, 100, 200, 300]);
      }

      startTitleFlash();
      void showLocalHiddenNotification(alert);
    },
    [showLocalHiddenNotification, startTitleFlash]
  );

  const handleAlert = useCallback(
    (alert) => {
      console.debug("[alerts sound] handle alert", {
        eventType: alert.eventType,
        visibility: document.visibilityState,
        symbol: alert.symbol,
        price: alert.price,
      });
      const deduplicationKey = buildAlertDeduplicationKey(alert);
      const now = Date.now();
      const lastSeenAt = recentAlertKeysRef.current.get(deduplicationKey);

      for (const [key, seenAt] of recentAlertKeysRef.current.entries()) {
        if (now - seenAt > ALERT_DEDUPLICATION_WINDOW_MS) {
          recentAlertKeysRef.current.delete(key);
        }
      }

      if (lastSeenAt && now - lastSeenAt < ALERT_DEDUPLICATION_WINDOW_MS) {
        return;
      }

      recentAlertKeysRef.current.set(deduplicationKey, now);
      setLastAlert(alert);
      setAlerts((current) => [alert, ...current].slice(0, 24));

      if (alert.eventType === "market_in_zone") {
        return;
      }

      if (document.visibilityState === "visible") {
        soundManager.play(alert.eventType);
        return;
      }

      queueBackgroundFallback(alert);
    },
    [queueBackgroundFallback]
  );

  const enablePushNotifications = useCallback(async () => {
    if (!user) {
      setPushDebugMessage("Push unavailable: no logged-in user");
      return false;
    }

    if (typeof Notification === "undefined") {
      setPushDebugMessage("Push unavailable: browser notifications are not supported");
      return false;
    }

    if (!("serviceWorker" in navigator)) {
      setPushDebugMessage("Push unavailable: service workers are not supported");
      return false;
    }

    if (!window.isSecureContext && window.location.hostname !== "localhost") {
      setPushDebugMessage("Push unavailable: HTTPS is required outside localhost");
      return false;
    }

    if (!hasFirebaseConfig()) {
      const missingKeys = getMissingFirebaseConfigKeys();
      setPushDebugMessage(
        `Push unavailable: missing ${missingKeys.join(", ")}. ${FIREBASE_ENV_HINT}`
      );
      return false;
    }

    if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) {
      setPushDebugMessage(
        `Push unavailable: REACT_APP_FIREBASE_VAPID_KEY is missing. ${FIREBASE_ENV_HINT}`
      );
      return false;
    }

    setPushDebugMessage("Requesting notification permission");
    const nextPermission = await Notification.requestPermission();
    setPermission(nextPermission);

    if (nextPermission !== "granted") {
      setPushDebugMessage(`Permission not granted: ${nextPermission}`);
      return false;
    }

    setPushDebugMessage("Initializing Firebase messaging");
    const messaging = await getFirebaseMessaging();

    if (!messaging) {
      setPushDebugMessage("Firebase messaging unavailable");
      return false;
    }

    setPushDebugMessage("Waiting for service worker");
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;
    swRegistrationRef.current = serviceWorkerRegistration;

    if (!serviceWorkerRegistration?.active) {
      setPushDebugMessage("Service worker is not active yet");
      return false;
    }

    setPushDebugMessage("Requesting FCM token");
    let token = null;

    try {
      token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration,
      });
    } catch (error) {
      console.debug("[push] getToken error", {
        message: error?.message,
        code: error?.code,
        name: error?.name,
      });
      setPushDebugMessage(
        `FCM token error: ${error?.message || error?.code || error?.name || "unknown error"}`
      );
      return false;
    }

    if (!token) {
      setPushDebugMessage("No FCM token returned");
      return false;
    }

    setPushDebugMessage("Registering token with backend");
    const response = await fetch(`${API_BASE_URL}/api/alerts/fcm/register`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        token,
        platform: "web",
      }),
    });

    if (!response.ok) {
      setPushDebugMessage(`Backend registration failed (${response.status})`);
      return false;
    }

    window.localStorage.setItem(FCM_TOKEN_KEY, token);
    window.localStorage.removeItem(PUSH_ONBOARDING_KEY);
    setPushEnabled(true);
    setShowPushOnboarding(false);
    setPushDebugMessage("Push notifications ready");
    return true;
  }, [user]);

  const dismissPushOnboarding = useCallback(() => {
    window.localStorage.removeItem(PUSH_ONBOARDING_KEY);
    setShowPushOnboarding(false);
  }, []);

  const triggerTestAlert = useCallback(() => {
    const testEvents = ["zone_approach", "spike_detected", "zone_failed"];
    const eventType = testEvents[testEventIndexRef.current % testEvents.length];
    const zoneType = eventType === "zone_failed" ? "supply" : "demand";
    const livePrice =
      Number.isFinite(Number(currentPrice)) && Number(currentPrice) > 0
        ? Number(currentPrice)
        : 1234.56;

    const sampleAlert = normalizeAlert(
      {
        id: `test-${Date.now()}`,
        symbol,
        timeframe,
        price: livePrice,
        zone: {
          type: zoneType,
        },
        createdAt: new Date().toISOString(),
        isTest: true,
      },
      eventType
    );

    testEventIndexRef.current += 1;
    console.debug("[alerts socket] test alert triggered", sampleAlert);
    handleAlert(sampleAlert);
  }, [currentPrice, handleAlert, symbol, timeframe]);

  const triggerTestSound = useCallback(() => {
    const testEvents = ["zone_approach", "market_in_zone", "spike_detected", "zone_failed"];
    console.debug("[alerts sound] test sound sequence triggered");
    testEvents.forEach((eventType, index) => {
      window.setTimeout(() => {
        soundManager.play(eventType);
      }, index * 700);
    });
  }, []);

  useEffect(() => {
    soundManager.initialize();

    return () => {
      if (zoneSoundIntervalRef.current) {
        window.clearInterval(zoneSoundIntervalRef.current);
        zoneSoundIntervalRef.current = null;
      }
      zoneSoundLoopKeyRef.current = "";
      soundManager.destroy();
      stopTitleFlash();
    };
  }, [stopTitleFlash]);

  useEffect(() => {
    const zoneOutcome = analysis?.zoneOutcome || null;
    const isZoneActive = zoneOutcome?.state === "ACTIVE";
    const entryKey = [
      symbol || "symbol",
      timeframe || "timeframe",
      zoneOutcome?.zoneId || "zone",
      zoneOutcome?.entryTime || "entry",
      zoneOutcome?.state || "state",
    ].join(":");
    const delayMs =
      Math.max(1, Number(zoneSoundDelaySeconds) || ZONE_SOUND_DELAY_SECONDS_DEFAULT) * 1000;

    console.debug("[alerts sound] zone evaluation", {
      trackerState: zoneOutcome?.state || null,
      zoneId: zoneOutcome?.zoneId || null,
      moveSize: zoneOutcome?.moveSize ?? null,
      entryKey,
      isZoneActive,
      delayMs,
      runningLoopKey: zoneSoundLoopKeyRef.current || null,
    });

    if (!isZoneActive) {
      if (zoneSoundIntervalRef.current) {
        window.clearInterval(zoneSoundIntervalRef.current);
        zoneSoundIntervalRef.current = null;
        console.debug("[alerts sound] zone timer cleared", {
          reason: zoneOutcome?.state || "inactive",
          entryKey: zoneSoundLoopKeyRef.current || null,
        });
      }
      zoneSoundLoopKeyRef.current = "";
      return undefined;
    }

    if (zoneSoundLoopKeyRef.current === entryKey && zoneSoundIntervalRef.current) {
      console.debug("[alerts sound] zone loop already running", { entryKey });
      return undefined;
    }

    if (zoneSoundIntervalRef.current) {
      window.clearInterval(zoneSoundIntervalRef.current);
      zoneSoundIntervalRef.current = null;
    }

    console.debug("[alerts sound] zone loop scheduled", {
      entryKey,
      delayMs,
      zoneId: zoneOutcome?.zoneId || null,
    });

    zoneSoundLoopKeyRef.current = entryKey;
    zoneSoundIntervalRef.current = window.setInterval(() => {
      console.debug("[alerts sound] zone sound playing", {
        entryKey,
        eventType: "market_in_zone",
      });
      soundManager.play("market_in_zone");
    }, delayMs);

    return () => {
      if (zoneSoundLoopKeyRef.current !== entryKey && zoneSoundIntervalRef.current) {
        window.clearInterval(zoneSoundIntervalRef.current);
        zoneSoundIntervalRef.current = null;
      }
    };
  }, [analysis, symbol, timeframe, zoneSoundDelaySeconds]);

  useEffect(() => {
    recentAlertKeysRef.current.clear();
    lastZoneOutcomeEventIdRef.current = "";
  }, [symbol, timeframe]);

  useEffect(() => {
    const trackerEvent = analysis?.zoneOutcome?.lastEvent;

    if (!trackerEvent?.id || lastZoneOutcomeEventIdRef.current === trackerEvent.id) {
      return;
    }

    lastZoneOutcomeEventIdRef.current = trackerEvent.id;
    handleAlert(normalizeAlert(trackerEvent, trackerEvent.eventType));
  }, [analysis, handleAlert]);

  useEffect(() => {
    if (!user || !symbol || !timeframe) {
      setSocketConnected(false);
      setSocketDebugMessage("Socket idle: missing user, symbol, or timeframe");
      return undefined;
    }

    console.debug("[alerts socket] initializing", {
      apiBaseUrl: API_BASE_URL,
      symbol,
      timeframe,
      userId: user?.id ?? null,
    });
    setSocketDebugMessage(`Connecting to ${API_BASE_URL}`);

    const socket = io(API_BASE_URL, {
      transports: ["polling", "websocket"],
      upgrade: true,
      reconnection: true,
      auth: {
        token: window.localStorage.getItem(SESSION_TOKEN_KEY),
      },
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.debug("[alerts socket] connected", {
        socketId: socket.id,
        connected: socket.connected,
        transport: socket.io.engine?.transport?.name,
      });
      setSocketConnected(true);
      setSocketDebugMessage(
        `Connected (${socket.id}) via ${socket.io.engine?.transport?.name || "unknown"}`
      );
      console.debug("[alerts socket] subscribing", { symbol, timeframe });
      socket.emit("alerts:subscribe", { symbol, timeframe });
    });

    socket.io.on("upgrade", (transport) => {
      console.debug("[alerts socket] transport upgraded", {
        transport: transport?.name,
      });
      setSocketDebugMessage(`Connected (${socket.id}) via ${transport?.name || "unknown"}`);
    });

    socket.on("disconnect", (reason) => {
      console.debug("[alerts socket] disconnected", {
        reason,
        socketId: socket.id,
      });
      setSocketConnected(false);
      setSocketDebugMessage(`Disconnected: ${reason || "unknown reason"}`);
      socketRef.current = null;
    });

    socket.on("connect_error", (error) => {
      console.debug("[alerts socket] connect_error", {
        message: error?.message,
        description: error?.description,
        context: error?.context,
        type: error?.type,
      });
      setSocketConnected(false);
      setSocketDebugMessage(
        `Connect error: ${error?.message || error?.description || "unknown error"}`
      );
    });

    socket.on("reconnect_attempt", (attempt) => {
      console.debug("[alerts socket] reconnect_attempt", { attempt });
      setSocketDebugMessage(`Reconnect attempt #${attempt}`);
    });

    socket.on("reconnect_error", (error) => {
      console.debug("[alerts socket] reconnect_error", {
        message: error?.message,
      });
      setSocketDebugMessage(
        `Reconnect error: ${error?.message || "unknown error"}`
      );
    });

    socket.on("reconnect_failed", () => {
      console.debug("[alerts socket] reconnect_failed");
      setSocketDebugMessage("Reconnect failed");
    });

    ["zone_approach", "spike_detected", "zone_failed"].forEach((eventName) => {
      socket.on(eventName, (payload) => {
        console.debug("[alerts socket] event received", {
          eventName,
          payload,
        });
        handleAlert(normalizeAlert(payload, eventName));
      });
    });

    return () => {
      console.debug("[alerts socket] cleanup", { symbol, timeframe });
      setSocketDebugMessage("Socket cleanup");
      socket.emit("alerts:unsubscribe", { symbol, timeframe });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [handleAlert, symbol, timeframe, user]);

  useEffect(() => {
    if (!user || !symbol || !timeframe) {
      setWatchSynced(false);
      return;
    }

    fetch(`${API_BASE_URL}/api/alerts/watchlist`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ symbol, timeframe }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to sync watchlist");
        }

        setWatchSynced(true);
      })
      .catch(() => {
        setWatchSynced(false);
      });
  }, [symbol, timeframe, user]);

  useEffect(() => {
    if (!user || !hasFirebaseConfig()) {
      return undefined;
    }

    let unsubscribe = () => {};

    getFirebaseMessaging().then((messaging) => {
      if (!messaging) {
        return;
      }

      unsubscribe = onMessage(messaging, (payload) => {
        console.debug("[alerts push] foreground payload received", payload);
        const alert = normalizeAlert(payload.data || {}, payload.data?.eventType);
        handleAlert(alert);
      });
    });

    return () => unsubscribe();
  }, [handleAlert, symbol, user]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        stopTitleFlash();
        document.title = ORIGINAL_TITLE;
        void dismissDeliveredNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [dismissDeliveredNotifications, stopTitleFlash]);

  const status = useMemo(
    () => ({
      permission,
      pushEnabled,
      showPushOnboarding,
      pushDebugMessage,
      socketConnected,
      socketDebugMessage,
      watchSynced,
      zoneSoundDelaySeconds,
    }),
    [
      permission,
      pushEnabled,
      showPushOnboarding,
      pushDebugMessage,
      socketConnected,
      socketDebugMessage,
      watchSynced,
      zoneSoundDelaySeconds,
    ]
  );

  return {
    alerts,
    lastAlert,
    status,
    enablePushNotifications,
    dismissPushOnboarding,
    setZoneSoundDelaySeconds,
    triggerTestAlert,
    triggerTestSound,
  };
}
